import React, { useCallback, useMemo, useState } from 'react';

import { GlobalAccount, GlobalAccountRoleType, GUID, PersonOrg, Tenant } from '@ndvr/customer-api';

import { FormSheetContent } from '@/components/FormSheet';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { CustomerApi } from 'API';
import { PersonFormFields } from 'modules';
import { getPersonName } from 'utils';
import { HouseholdPersonFormValues, PersonFormValues } from 'views/person/person-form-model';
import { PotentialDuplicateDialog } from 'views/person/PotentialDuplicateDialog';
import { checkPersonDuplicates, usePotentialPersonDuplicates } from 'views/person/usePotentialPersonDuplicates';

import { useAddPersonToHouseholdWithRole } from '../views/household/household-queries';
import {
    LinkPersonToHouseholdRoleFields,
    linkPersonToHouseholdRoleFormSchema,
} from '../views/household/LinkPersonToHouseholdDrawer';
import { getPersonFormDefaultValues, PersonLink, SavePersonContext } from '../views/person/person-form-utils';
import { usePersonOrgProfile, useSavePersonProfile } from '../views/person/person-queries';

type PersonDrawerProps =
    | {
          onClose: () => void;
          mode: 'edit';
          personGUID: GUID<PersonOrg>;
          invalidateGlobalAccountGUID?: GUID<GlobalAccount>;
      }
    | {
          onClose: () => void;
          mode: 'create-household';
          globalAccountGUID: GUID<GlobalAccount>;
          link?: PersonLink;
          defaultValues?: HouseholdPersonFormValues;
      }
    | { onClose: () => void; mode: 'create-tenant'; tenantGUID: GUID<Tenant> };

export const PersonDrawer = (props: PersonDrawerProps) => {
    const saveContext: SavePersonContext =
        props.mode === 'edit' ? { mode: 'edit', personGUID: props.personGUID } : props;
    const personGUID = props.mode === 'edit' ? props.personGUID : undefined;
    const isCreateHousehold = props.mode === 'create-household';
    const globalAccountGUID = isCreateHousehold ? props.globalAccountGUID : undefined;

    const { data: existingPerson, isPending } = usePersonOrgProfile(personGUID);
    const { mutate: savePersonProfile, isPending: saveInProgress } = useSavePersonProfile(saveContext, {
        invalidateGlobalAccountGUID: props.mode === 'edit' ? props.invalidateGlobalAccountGUID : undefined,
    });
    const { mutate: addPersonToHouseholdWithRole, isPending: linking } = useAddPersonToHouseholdWithRole();

    // Frozen on mount: Form resets to defaultValues whenever the reference changes, so a caller
    // that rebuilds the prefill object each render must not wipe the user's edits.
    const [prefill] = useState(() => (props.mode === 'create-household' ? props.defaultValues : undefined));
    const defaultValues: PersonFormValues = useMemo(() => {
        return existingPerson ? getPersonFormDefaultValues(existingPerson) : (prefill ?? {});
    }, [existingPerson, prefill]);

    const duplicates = usePotentialPersonDuplicates(
        JSON.stringify([
            props.mode,
            personGUID,
            globalAccountGUID,
            props.mode === 'create-tenant' ? props.tenantGUID : undefined,
        ]),
    );

    const { check: checkDuplicates } = duplicates;

    const onSubmit = useCallback(
        (formData: HouseholdPersonFormValues, close: () => void) => {
            const { accountRoleType, accountRoleLabel, ...personData } = formData;

            const create = () =>
                savePersonProfile(personData, {
                    onSuccess: (result) => {
                        if (isCreateHousehold && globalAccountGUID && result?.personOrgGUID && accountRoleType) {
                            const roleLabel = accountRoleType === 'Custom' ? accountRoleLabel : accountRoleType;
                            addPersonToHouseholdWithRole(
                                {
                                    globalAccountGUID,
                                    personOrgGUID: result.personOrgGUID,
                                    roleType: GlobalAccountRoleType.SECONDARY_USER,
                                    roleLabel,
                                },
                                { onSuccess: close },
                            );
                        } else {
                            close();
                        }
                    },
                });
            if (props.mode === 'edit') {
                create();
                return;
            }
            const context = props;
            void checkDuplicates(async () => {
                const tenantGUID =
                    context.mode === 'create-tenant'
                        ? context.tenantGUID
                        : (await CustomerApi({ hideError: true }).getGlobalAccount(context.globalAccountGUID))[0]
                              ?.tenantGUID;
                if (!tenantGUID) throw new Error('Client base could not be resolved');
                return checkPersonDuplicates(tenantGUID, personData);
            }, create);
        },
        [savePersonProfile, addPersonToHouseholdWithRole, isCreateHousehold, globalAccountGUID, props, checkDuplicates],
    );

    return (
        <>
            <Sheet
                defaultOpen
                onExitComplete={props.onClose}
                onOpenChange={(open) => {
                    if (!open) duplicates.cancel();
                }}
            >
                <SheetContent layout="scroll-body" size="lg" aria-describedby={undefined}>
                    <FormSheetContent
                        title={personGUID ? `Edit ${getPersonName(existingPerson?.personOrg)}` : 'New Person'}
                        onSubmit={onSubmit}
                        defaultValues={defaultValues}
                        formSchema={isCreateHousehold ? linkPersonToHouseholdRoleFormSchema : undefined}
                        isLoading={!!personGUID && isPending}
                        pending={saveInProgress || linking || duplicates.busy}
                        submitLabel={personGUID ? 'Save' : 'Create'}
                    >
                        <fieldset disabled={saveInProgress || linking || duplicates.busy} className="contents">
                            {isCreateHousehold && <LinkPersonToHouseholdRoleFields />}
                            <PersonFormFields />
                        </fieldset>
                    </FormSheetContent>
                </SheetContent>
            </Sheet>
            <PotentialDuplicateDialog
                result={duplicates.result}
                onBack={duplicates.cancel}
                onProceed={duplicates.proceed}
            />
        </>
    );
};
